import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProject, deleteProject, updateProjectTasks, projectPdfUrl, projectCsvUrl, projectGanUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import GanttChart from "@/components/GanttChart";
import FeedbackPanel from "@/components/FeedbackPanel";
import { CORPS_COLORS, CORPS_LIST, addDays, fmtDate, colorFor, taskStatus } from "@/lib/corpsColors";
import {
  ArrowLeft,
  Download,
  FileText,
  Trash2,
  Search,
  Calendar,
  Ruler,
  Euro,
  AlertTriangle,
  AlertCircle,
  Loader2,
  Zap,
  Pencil,
  Save,
  X,
  Trash,
} from "lucide-react";

const ZOOM_OPTIONS = [
  { key: "jour", label: "Jours" },
  { key: "semaine", label: "Semaines" },
  { key: "mois", label: "Mois" },
];

export default function ProjectView() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState("semaine");
  const [showCritical, setShowCritical] = useState(true);
  const [filters, setFilters] = useState(new Set());
  const [search, setSearch] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editedTasks, setEditedTasks] = useState(null); // local mutable copy
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let m = true;
    getProject(id)
      .then((p) => m && setProject(p))
      .catch(() => toast.error("Projet introuvable"))
      .finally(() => m && setLoading(false));
    return () => (m = false);
  }, [id]);

  const enterEdit = () => {
    setEditedTasks(project.tasks.map((t) => ({ ...t })));
    setEditMode(true);
  };
  const cancelEdit = () => {
    setEditedTasks(null);
    setEditMode(false);
  };
  const applyTaskChange = (taskId, { start_day, duration_days }) => {
    setEditedTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, start_day, duration_days } : t))
    );
  };
  const deleteTask = (taskId) => {
    setEditedTasks((prev) => prev.filter((t) => t.id !== taskId));
  };
  const setTaskField = (taskId, field, value) => {
    setEditedTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, [field]: value } : t))
    );
  };
  // Convert date input <-> day offset from project start
  const dateToDay = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    const s = new Date(project.start_date + "T00:00:00");
    return Math.round((d - s) / 86400000);
  };
  const dayToDate = (offset) => {
    const d = new Date(project.start_date + "T00:00:00");
    d.setDate(d.getDate() + offset);
    return d.toISOString().slice(0, 10);
  };
  const saveEdits = async () => {
    setSaving(true);
    try {
      const updated = await updateProjectTasks(id, editedTasks);
      setProject(updated);
      setEditedTasks(null);
      setEditMode(false);
      toast.success("Planning mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const toggleFilter = (c) => {
    const next = new Set(filters);
    next.has(c) ? next.delete(c) : next.add(c);
    setFilters(next);
  };

  const filteredTasks = useMemo(() => {
    if (!project) return [];
    const source = editMode && editedTasks ? editedTasks : project.tasks;
    return source
      .filter((t) => (filters.size === 0 ? true : filters.has(t.corps_etat)))
      .filter((t) =>
        search.trim()
          ? t.name.toLowerCase().includes(search.toLowerCase()) ||
            (t.description || "").toLowerCase().includes(search.toLowerCase())
          : true
      );
  }, [project, filters, search, editMode, editedTasks]);

  const currentTotalDays = useMemo(() => {
    const source = editMode && editedTasks ? editedTasks : (project?.tasks || []);
    let total = 0;
    for (const t of source) {
      const end = (t.start_day || 0) + (t.duration_days || 0);
      if (end > total) total = end;
    }
    return total || project?.total_duration_days || 0;
  }, [editMode, editedTasks, project]);

  const lateCount = useMemo(() => {
    if (!project) return 0;
    const source = editMode && editedTasks ? editedTasks : project.tasks;
    return source.filter((t) => taskStatus(t, project.start_date) === "late").length;
  }, [project, editMode, editedTasks]);

  const usedCorps = useMemo(() => {
    if (!project) return [];
    const set = new Set(project.tasks.map((t) => t.corps_etat));
    return CORPS_LIST.filter((c) => set.has(c));
  }, [project]);

  const doDelete = async () => {
    if (!window.confirm("Supprimer ce planning ?")) return;
    await deleteProject(id);
    toast.success("Planning supprimé");
    window.location.href = "/historique";
  };

  const exportCSV = () => {
    if (!project) return;
    const rows = [
      ["Tâche", "Corps d'état", "Début", "Fin", "Durée (j)", "Dépendances", "Critique"],
    ];
    project.tasks.forEach((t) => {
      const start = addDays(project.start_date, t.start_day);
      const end = addDays(project.start_date, t.start_day + t.duration_days);
      rows.push([
        t.name,
        t.corps_etat,
        fmtDate(start),
        fmtDate(end),
        t.duration_days,
        (t.dependencies || []).join(","),
        t.is_critical ? "oui" : "non",
      ]);
    });
    const csv = rows
      .map((r) => r.map((v) => `"${String(v).replaceAll('"', '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${project.name.replace(/[^\w-]+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading)
    return (
      <div className="max-w-5xl mx-auto px-6 py-20 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#0F2137]" />
      </div>
    );

  if (!project)
    return (
      <div className="max-w-5xl mx-auto px-6 py-20 text-center">
        <p className="text-slate-600">Projet introuvable.</p>
        <Link to="/">
          <Button className="mt-4">Retour à l'accueil</Button>
        </Link>
      </div>
    );

  const endDate = addDays(project.start_date, project.total_duration_days);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="project-view">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <Link to="/historique" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F2137] mb-2">
            <ArrowLeft className="w-4 h-4" /> Historique
          </Link>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0F2137]" data-testid="project-title">
            {project.name}
          </h1>
          <div className="flex flex-wrap gap-2 mt-3">
            <Badge variant="secondary" className="capitalize">{project.project_type}</Badge>
            {project.surface_m2 && (
              <Badge variant="secondary">
                <Ruler className="w-3 h-3 mr-1" />
                {project.surface_m2} m²
              </Badge>
            )}
            <Badge variant="secondary">
              <Calendar className="w-3 h-3 mr-1" />
              {fmtDate(new Date(project.start_date + "T00:00:00"))} → {fmtDate(endDate)}
            </Badge>
            <Badge className="bg-[#0F2137] text-white hover:bg-[#0F2137]">
              <Zap className="w-3 h-3 mr-1" />
              {project.total_duration_days} jours
            </Badge>
            {lateCount > 0 && (
              <Badge
                data-testid="late-count-badge"
                className="bg-red-600 text-white hover:bg-red-600 animate-pulse"
              >
                <AlertCircle className="w-3 h-3 mr-1" />
                {lateCount} tâche{lateCount > 1 ? "s" : ""} en retard
              </Badge>
            )}
            {project.budget_estime ? (
              <Badge variant="secondary">
                <Euro className="w-3 h-3 mr-1" />
                {Number(project.budget_estime).toLocaleString("fr-FR")} €
              </Badge>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={projectPdfUrl(id)} target="_blank" rel="noreferrer" data-testid="btn-export-pdf">
            <Button variant="outline">
              <FileText className="w-4 h-4 mr-1.5" /> Export PDF
            </Button>
          </a>
          <a href={projectCsvUrl(id)} target="_blank" rel="noreferrer" data-testid="btn-export-csv">
            <Button variant="outline">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
          </a>
          <a href={projectGanUrl(id)} target="_blank" rel="noreferrer" data-testid="btn-export-gan">
            <Button variant="outline">
              <Download className="w-4 h-4 mr-1.5" /> Export .gan
            </Button>
          </a>
          {!editMode ? (
            <Button
              onClick={enterEdit}
              data-testid="btn-edit"
              className="bg-[#0F2137] hover:bg-[#1E293B] text-white"
            >
              <Pencil className="w-4 h-4 mr-1.5" /> Éditer
            </Button>
          ) : (
            <>
              <Button
                onClick={cancelEdit}
                variant="outline"
                data-testid="btn-cancel-edit"
              >
                <X className="w-4 h-4 mr-1.5" /> Annuler
              </Button>
              <Button
                onClick={saveEdits}
                disabled={saving}
                data-testid="btn-save-edit"
                className="bg-[#16A34A] hover:bg-[#15803D] text-white"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1.5" />
                )}
                Enregistrer
              </Button>
            </>
          )}
          <Button
            variant="outline"
            onClick={doDelete}
            data-testid="btn-delete"
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer
          </Button>
        </div>
      </div>

      {/* Summary */}
      {project.summary && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 mb-6" data-testid="project-summary">
          <div className="font-mono-tech text-xs uppercase tracking-wider text-slate-500 mb-2">
            Synthèse IA
          </div>
          <p className="text-slate-700 leading-relaxed">{project.summary}</p>
        </div>
      )}

      <div className="mb-6">
        <FeedbackPanel
          projectId={id}
          existing={project.feedback}
          onSubmitted={(fb) => setProject((p) => ({ ...p, feedback: fb }))}
        />
      </div>

      {project.contraintes && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-6 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-900 text-sm">Contraintes</div>
            <div className="text-sm text-amber-800 mt-0.5">{project.contraintes}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une tâche..."
            className="pl-9"
          />
        </div>
        <div className="flex gap-1 bg-white rounded-lg border border-slate-200 p-1" data-testid="zoom-toggle">
          {ZOOM_OPTIONS.map((z) => (
            <button
              key={z.key}
              data-testid={`zoom-${z.key}`}
              onClick={() => setZoom(z.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                zoom === z.key ? "bg-[#0F2137] text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {z.label}
            </button>
          ))}
        </div>
        <button
          data-testid="toggle-critical"
          onClick={() => setShowCritical((v) => !v)}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
            showCritical
              ? "bg-[#0F2137] text-white border-[#0F2137]"
              : "bg-white text-slate-600 border-slate-200"
          }`}
        >
          Chemin critique
        </button>
      </div>

      <div className="mb-5 flex flex-wrap gap-2" data-testid="corps-filters">
        {usedCorps.map((c) => {
          const active = filters.has(c);
          return (
            <button
              key={c}
              data-testid={`filter-${c}`}
              onClick={() => toggleFilter(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                active
                  ? "text-white border-transparent"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
              style={active ? { background: CORPS_COLORS[c] } : {}}
            >
              <span
                className="inline-block w-2 h-2 rounded-full mr-1.5"
                style={{ background: CORPS_COLORS[c] }}
              />
              {c}
            </button>
          );
        })}
        {filters.size > 0 && (
          <button
            onClick={() => setFilters(new Set())}
            data-testid="filter-clear"
            className="text-xs text-slate-500 hover:text-[#0F2137] underline underline-offset-4 ml-1"
          >
            Effacer filtres
          </button>
        )}
      </div>

      <Tabs defaultValue="gantt" className="w-full">
        <TabsList data-testid="view-tabs">
          <TabsTrigger value="gantt" data-testid="tab-gantt">Diagramme de Gantt</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list">Liste des tâches</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">Documents ({project.documents.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="gantt" className="mt-4">
          {editMode && (
            <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-900 flex items-center gap-2" data-testid="edit-hint">
              <Pencil className="w-4 h-4" />
              Mode édition : glissez une barre pour la déplacer, tirez le bord droit pour ajuster la durée. <strong>Alt/Shift + glisser d'une barre vers une autre</strong> = créer une précédence. N'oubliez pas d'enregistrer.
            </div>
          )}
          <GanttChart
            tasks={filteredTasks}
            startDate={project.start_date}
            totalDays={currentTotalDays}
            zoom={zoom}
            showCritical={showCritical}
            editable={editMode}
            onTaskChange={applyTaskChange}
            onLinkCreate={(fromId, toId) => {
              setEditedTasks((prev) => prev.map((t) => {
                if (t.id !== toId || fromId === toId) return t;
                const deps = new Set(t.dependencies || []);
                deps.add(fromId);
                return { ...t, dependencies: [...deps] };
              }));
              toast.success("Lien de précédence ajouté");
            }}
          />
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="task-list">
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 px-4 py-3 border-b border-slate-200 font-mono-tech text-[10px] uppercase tracking-wider text-slate-500">
              <div>Tâche</div>
              <div>Début → Fin</div>
              <div>Durée</div>
              <div>Statut</div>
            </div>
            {filteredTasks.map((t) => {
              const start = addDays(project.start_date, t.start_day);
              const end = addDays(project.start_date, t.start_day + t.duration_days);
              const status = taskStatus(t, project.start_date);
              const late = status === "late";

              if (editMode) {
                return (
                  <div
                    key={t.id}
                    data-testid={`task-row-${t.id}`}
                    className="px-4 py-3 border-b border-slate-100 last:border-0 bg-white hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: colorFor(t.corps_etat) }}
                      />
                      <span className="font-medium text-[#0F2137]">{t.name}</span>
                      <Badge variant="secondary" className="text-[10px]">{t.corps_etat}</Badge>
                      <button
                        onClick={() => deleteTask(t.id)}
                        data-testid={`delete-task-${t.id}`}
                        className="ml-auto text-slate-400 hover:text-red-600 p-1"
                        title="Supprimer cette tâche"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <label className="text-xs">
                        <span className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-500 block mb-0.5">
                          Début
                        </span>
                        <Input
                          type="date"
                          data-testid={`start-${t.id}`}
                          value={dayToDate(t.start_day)}
                          onChange={(e) => {
                            const newStart = dateToDay(e.target.value);
                            if (!Number.isNaN(newStart)) setTaskField(t.id, "start_day", Math.max(0, newStart));
                          }}
                          className="h-8 text-sm"
                        />
                      </label>
                      <label className="text-xs">
                        <span className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-500 block mb-0.5">
                          Fin
                        </span>
                        <Input
                          type="date"
                          data-testid={`end-${t.id}`}
                          value={dayToDate(t.start_day + t.duration_days)}
                          onChange={(e) => {
                            const endDay = dateToDay(e.target.value);
                            const dur = Math.max(1, endDay - t.start_day);
                            setTaskField(t.id, "duration_days", dur);
                          }}
                          className="h-8 text-sm"
                        />
                      </label>
                      <label className="text-xs">
                        <span className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-500 block mb-0.5">
                          Durée (jours)
                        </span>
                        <Input
                          type="number"
                          min="1"
                          data-testid={`duration-${t.id}`}
                          value={t.duration_days}
                          onChange={(e) => {
                            const d = Math.max(1, parseInt(e.target.value || "1", 10));
                            setTaskField(t.id, "duration_days", d);
                          }}
                          className="h-8 text-sm"
                        />
                      </label>
                    </div>
                    <div className="mt-2">
                      <span className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-500 block mb-1">
                        Tâches antérieures (précédences)
                      </span>
                      <div className="flex flex-wrap gap-1.5" data-testid={`deps-${t.id}`}>
                        {(editedTasks || []).filter((o) => o.id !== t.id).map((o) => {
                          const active = (t.dependencies || []).includes(o.id);
                          return (
                            <button
                              key={o.id}
                              type="button"
                              onClick={() => {
                                const deps = new Set(t.dependencies || []);
                                if (active) deps.delete(o.id); else deps.add(o.id);
                                setTaskField(t.id, "dependencies", [...deps]);
                              }}
                              className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
                                active
                                  ? "bg-[#0F2137] text-white border-[#0F2137]"
                                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                              }`}
                            >
                              {active ? "✓ " : "+ "}{o.name.length > 30 ? o.name.slice(0, 30) + "…" : o.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={t.id}
                  data-testid={`task-row-${t.id}`}
                  className={`grid grid-cols-[1fr_auto_auto_auto] gap-4 px-4 py-3 border-b border-slate-100 last:border-0 items-center hover:bg-slate-50 ${late ? "bg-red-50/60" : ""}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: colorFor(t.corps_etat) }}
                      />
                      <span className="font-medium text-[#0F2137]">{t.name}</span>
                      <Badge variant="secondary" className="text-[10px]">{t.corps_etat}</Badge>
                      {late && (
                        <Badge className="bg-red-600 text-white hover:bg-red-600 text-[10px]" data-testid={`late-badge-${t.id}`}>
                          <AlertCircle className="w-3 h-3 mr-0.5" /> En retard
                        </Badge>
                      )}
                    </div>
                    {t.description && (
                      <div className="text-xs text-slate-500 mt-1 line-clamp-1">{t.description}</div>
                    )}
                  </div>
                  <div className="text-xs font-mono-tech text-slate-600 whitespace-nowrap">
                    {fmtDate(start)} → {fmtDate(end)}
                  </div>
                  <div className="text-sm font-semibold text-[#0F2137] whitespace-nowrap">
                    {t.duration_days}j
                  </div>
                  <div>
                    {t.is_critical ? (
                      <Badge className="bg-red-50 text-red-700 border-red-200 hover:bg-red-50">
                        Critique
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Standard</Badge>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredTasks.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">Aucune tâche.</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="docs" className="mt-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="docs-panel">
            {project.documents.length === 0 && (
              <div className="text-sm text-slate-500 text-center py-6">
                Aucun document déposé pour ce projet.
              </div>
            )}
            <div className="space-y-3">
              {project.documents.map((d) => (
                <div key={d.id} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-[#0F2137]">{d.filename}</div>
                    <Badge variant="secondary" className="font-mono-tech text-[10px]">
                      {(d.size / 1024).toFixed(0)} ko
                    </Badge>
                  </div>
                  {d.excerpt && (
                    <div className="text-xs text-slate-500 mt-2 font-mono-tech line-clamp-3">
                      {d.excerpt}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
